import { REST_API_METHOD, REST_API_CONTROLLER } from './apiAdminUrls';

const { PSP_ADMIN, WALLET_OPERATION_ADMIN, USER_ADMIN, TRADING_ACCOUNT_ADMIN } = REST_API_CONTROLLER;
const { GET_WITH_STAFF, EXPORT_WITH_STAFF, GRID_WITH_STAFF, GET_UNMASKED, UPDATE_UNMASKED } = REST_API_METHOD;
const { GRID_WITH_STAFF_TOTALS } = REST_API_METHOD;

type TApiBase = {
  [groupName: string]: {
    [operationName: string]: string;
  };
};
const API_ORIGIN = window.apiOriginUrl;
const buildApiOriginUrls = <T extends TApiBase>(api: T) => {
  if (!API_ORIGIN) {
    return api;
  }

  Object.keys(api).forEach((groupName) => {
    const group = api[groupName];

    Object.keys(group).forEach((operationName) => {
      const operation = group[operationName];

      group[operationName] = new URL(operation, API_ORIGIN).toString();
    });
  });
  return api;
};

export const ApiUrls = buildApiOriginUrls({
  account: {
    registerShortLinkClick: '/api.account.registerShortLinkClick',
    registration: '/api.account.register',
    changeEmail: '/api.account.changeEmail',
    confirmChangeEmail: '/api.account.confirmChangeEmail',
    signIn: '/api.account.signIn',
    signOut: '/api.account.signOut',
    changePinCode: '/api.account.changePinCode',
    resetPinCode: '/api.account.resetPinCode',
    changePassword: '/api.account.changePassword',
    passwordRecovery: '/api.account.passwordRecovery',
    forgotPassword: '/api.account.forgotPassword',
    getUserSettings: '/api.account.getUserSettings',
    getUserSettingsInitialAuth: '/api.account.getUserSettings?initialAuth',
    validateToken: '/api.account.validateToken',
    validateNavigationReferrer: '/api.account.validateNavigationReferrer',
    getIpInfo: '/api.account.getIpInfo',
    getSessionExpirationInformation: '/api.account.getSessionExpirationInformation',
    requestOtp: '/api.account.requestOtp',
  },

  access: {
    checkApiAccess: '/api.access_admin.checkApiAccess',
  },

  tradingServerAdmin: {
    grid: '/api.tradingServer_admin.grid',
    export: '/api.tradingServer_admin.export',
    exportUnmasked: '/api.tradingServer_admin.exportUnmasked',
    create: '/api.tradingServer_admin.create',
    delete: '/api.tradingServer_admin.delete',
    enable: '/api.tradingServer_admin.enable',
    update: '/api.tradingServer_admin.update',
    updateUnmasked: '/api.tradingServer_admin.updateUnmasked',
    get: '/api.tradingServer_admin.get',
    getUnmasked: '/api.tradingServer_admin.getUnmasked',
    testwebApi: '/api.tradingServer_admin.testWebApi',
    testdb: '/api.tradingServer_admin.testDb',
    getList: '/api.tradingServer_admin.getList',
  },

  tradingServerUser: {
    getList: '/api.tradingServer_user.GetList',
  },

  tradingServerHealthCheck: {
    grid: '/api.tradingServerHealthCheck_admin.grid',
    testWebApi: '/api.tradingServerHealthCheck_admin.testWebApi',
    testDb: '/api.tradingServerHealthCheck_admin.testDb',
  },

  bankAdmin: {
    grid: '/api.bank_admin.grid',
    export: '/api.bank_admin.export',
    create: '/api.bank_admin.create',
    delete: '/api.bank_admin.delete',
    update: '/api.bank_admin.update',
    get: '/api.bank_admin.get',
    getInfo: '/api.bank_admin.getInfo',
    getList: '/api.bank_admin.getList',
    isProhibited: '/api.bank_admin.isProhibited',
  },

  userBankAccountAdmin: {
    addComment: '/api.userBankAccount_admin.addComment',
    grid: '/api.userBankAccount_admin.grid',
    export: '/api.userBankAccount_admin.export',
    promote: '/api.userBankAccount_admin.promote',
    delete: '/api.userBankAccount_admin.delete',
    get: '/api.userBankAccount_admin.get',
    getInfo: '/api.userBankAccount_admin.getInfo',
    create: '/api.userBankAccount_admin.create',
    update: '/api.userBankAccount_admin.update',
    getDeclineReasonText: '/api.userBankAccount_admin.getDeclineReasonText',
    download: '/api.userBankAccount_admin.download',
    show: '/api.userBankAccount_admin.show',
    isBankAccountUnique: '/api.userBankAccount_admin.isBankAccountUnique',
  },

  dataChangeRequestAdmin: {
    grid: '/api.dataChangeRequest_admin.grid',
    export: '/api.dataChangeRequest_admin.export',
    get: '/api.dataChangeRequest_admin.get',
    getInfo: '/api.dataChangeRequest_admin.getInfo',
    promote: '/api.dataChangeRequest_admin.promote',
    download: '/api.dataChangeRequest_admin.download',
  },

  digitalWalletAdmin: {
    grid: '/api.digitalwallet_admin.grid',
    export: '/api.digitalwallet_admin.export',
    get: '/api.digitalwallet_admin.get',
    getInfo: '/api.digitalwallet_admin.getInfo',
    getList: '/api.digitalwallet_admin.getList',
    create: '/api.digitalwallet_admin.create',
    delete: '/api.digitalwallet_admin.delete',
    update: '/api.digitalwallet_admin.update',
  },

  digitalWalletAccountAdmin: {
    grid: '/api.userdigitalWalletAccount_admin.grid',
    export: '/api.userdigitalWalletAccount_admin.export',
    download: '/api.userdigitalWalletAccount_admin.download',
    get: '/api.userdigitalWalletAccount_admin.get',
    getInfo: '/api.userdigitalWalletAccount_admin.getInfo',
    create: '/api.userdigitalWalletAccount_admin.create',
    update: '/api.userdigitalWalletAccount_admin.update',
    delete: '/api.userdigitalWalletAccount_admin.delete',
    addComment: '/api.userdigitalWalletAccount_admin.addComment',
    promote: '/api.userdigitalWalletAccount_admin.promote',
    getDeclineReasonText: '/api.userdigitalWalletAccount_admin.getDeclineReasonText',
  },

  userCardAdmin: {
    grid: '/api.userCard_admin.grid',
    export: '/api.userCard_admin.export',
    create: '/api.userCard_admin.create',
    get: '/api.userCard_admin.get',
    delete: '/api.userCard_admin.delete',
    download: '/api.userCard_admin.download',
    promote: '/api.userCard_admin.promote',
    getDeclineReasonText: '/api.userCard_admin.getDeclineReasonText',
    update: '/api.userCard_admin.update',
    addComment: '/api.userCard_admin.addComment',
  },

  permissionAdmin: {
    grid: '/api.permission_admin.grid',
    export: '/api.permission_admin.export',
    getList: '/api.permission_admin.getList',
    get: '/api.permission_admin.get',
    getInfo: '/api.permission_admin.getInfo',
    create: '/api.permission_admin.create',
    update: '/api.permission_admin.update',
    delete: '/api.permission_admin.delete',
  },

  userPermission: {
    grid: '/api.userPermission_admin.grid',
    get: '/api.userPermission_admin.get',
    getOperations: '/api.userPermission_admin.getOperations',
    getUserList: '/api.userPermission_admin.getUserList',
  },

  operationGroup: {
    getOperations: '/api.operationGroup_admin.getOperations',
    update: '/api.operationGroup_admin.update',
  },

  customTagAdmin: {
    grid: '/api.customTag_admin.grid',
    export: '/api.customTag_admin.export',
    getList: '/api.customTag_admin.getList',
    get: '/api.customTag_admin.get',
    getInfo: '/api.customTag_admin.getInfo',
    create: '/api.customTag_admin.create',
    update: '/api.customTag_admin.update',
    delete: '/api.customTag_admin.delete',
  },

  userCustomTagAdmin: {
    grid: '/api.userCustomTag_admin.grid',
    export: '/api.userCustomTag_admin.export',
    getList: '/api.userCustomTag_admin.getList',
    get: '/api.userCustomTag_admin.get',
    create: '/api.userCustomTag_admin.create',
    update: '/api.userCustomTag_admin.update',
    delete: '/api.userCustomTag_admin.delete',
  },

  documentConfigurationAdmin: {
    grid: '/api.documentConfiguration_admin.grid',
    export: '/api.documentConfiguration_admin.export',
    get: '/api.documentConfiguration_admin.get',
    getInfo: '/api.documentConfiguration_admin.getInfo',
    create: '/api.documentConfiguration_admin.create',
    clone: '/api.documentConfiguration_admin.clone',
    update: '/api.documentConfiguration_admin.update',
    delete: '/api.documentConfiguration_admin.delete',
  },

  documentConfigurationItemAdmin: {
    grid: '/api.documentConfigurationItem_admin.grid',
    export: '/api.documentConfigurationItem_admin.export',
    get: '/api.documentConfigurationItem_admin.get',
    create: '/api.documentConfigurationItem_admin.create',
    update: '/api.documentConfigurationItem_admin.update',
    delete: '/api.documentConfigurationItem_admin.delete',
  },

  documentConfigurationUser: {
    get: '/api.documentConfiguration_user.get',
    getTypes: '/api.documentConfiguration_user.getTypes',
  },

  bannerConfigurationAdmin: {
    grid: '/api.bannerConfiguration_admin.grid',
    export: '/api.bannerConfiguration_admin.export',
    get: '/api.bannerConfiguration_admin.get',
    getInfo: '/api.bannerConfiguration_admin.getInfo',
    update: '/api.bannerConfiguration_admin.update',
    create: '/api.bannerConfiguration_admin.create',
    delete: '/api.bannerConfiguration_admin.delete',
    enable: '/api.bannerConfiguration_admin.enable',
  },

  bannerConfigurationUser: {
    get: '/api.bannerConfiguration_user.get',
    download: '/api.bannerConfiguration_user.download',
  },

  bannerConfigurationFileAdmin: {
    grid: '/api.bannerConfigurationFile_admin.grid',
    create: '/api.bannerConfigurationFile_admin.create',
    delete: '/api.bannerConfigurationFile_admin.delete',
    get: '/api.bannerConfigurationFile_admin.get',
    download: '/api.bannerConfigurationFile_admin.download',
    update: '/api.bannerConfigurationFile_admin.update',
  },

  videoConfigurationAdmin: {
    grid: '/api.videoConfiguration_admin.grid',
    export: '/api.videoConfiguration_admin.export',
    update: '/api.videoConfiguration_admin.update',
    create: '/api.videoConfiguration_admin.create',
    delete: '/api.videoConfiguration_admin.delete',
    get: '/api.videoConfiguration_admin.get',
    getInfo: '/api.videoConfiguration_admin.getInfo',
    enable: '/api.videoConfiguration_admin.enable',
    getI18N: '/api.videoConfiguration_admin.getI18N',
    updateI18N: '/api.videoConfiguration_admin.updateI18N',
  },

  videoConfigurationLinkAdmin: {
    grid: '/api.videoConfigurationLink_admin.grid',
    export: '/api.videoConfigurationLink_admin.export',
    get: '/api.videoConfigurationLink_admin.get',
    create: '/api.videoConfigurationLink_admin.create',
    delete: '/api.videoConfigurationLink_admin.delete',
    update: '/api.videoConfigurationLink_admin.update',
  },

  videoConfigurationUser: {
    get: '/api.videoConfiguration_user.get',
    getByLocation: '/api.videoConfiguration_user.getByLocation',
    getList: '/api.videoConfiguration_user.getList',
    getListByCategory: '/api.videoConfiguration_user.getListByCategory',
  },

  salesLeadAdmin: {
    get: '/api.salesLead_admin.get',
    grid: '/api.salesLead_admin.grid',
    export: '/api.salesLead_admin.export',
  },

  onboardingConfigurationAdmin: {
    grid: '/api.onboardingConfiguration_admin.grid',
    export: '/api.onboardingConfiguration_admin.export',
    update: '/api.onboardingConfiguration_admin.update',
    create: '/api.onboardingConfiguration_admin.create',
    clone: '/api.onboardingConfiguration_admin.clone',
    delete: '/api.onboardingConfiguration_admin.delete',
    get: '/api.onboardingConfiguration_admin.get',
    getInfo: '/api.onboardingConfiguration_admin.getInfo',
  },

  onboardingConfigurationUser: {
    get: '/api.onboardingConfiguration_user.get',
  },

  userGroupAdmin: {
    grid: '/api.userGroup_admin.grid',
    export: '/api.userGroup_admin.export',
    get: '/api.userGroup_admin.get',
    create: '/api.userGroup_admin.create',
    update: '/api.userGroup_admin.update',
    delete: '/api.userGroup_admin.delete',
  },

  userCountryAccessAdmin: {
    grid: '/api.userCountryAccess_admin.grid',
    export: '/api.userCountryAccess_admin.export',
    get: '/api.userCountryAccess_admin.get',
    getInfo: '/api.userCountryAccess_admin.getInfo',
    create: '/api.userCountryAccess_admin.create',
    update: '/api.userCountryAccess_admin.update',
    delete: '/api.userCountryAccess_admin.delete',
  },

  eventTypeGroupAdmin: {
    grid: '/api.eventTypeGroup_admin.grid',
    export: '/api.eventTypeGroup_admin.export',
    get: '/api.eventTypeGroup_admin.get',
    create: '/api.eventTypeGroup_admin.create',
    update: '/api.eventTypeGroup_admin.update',
    delete: '/api.eventTypeGroup_admin.delete',
  },

  informGroupAdmin: {
    grid: '/api.informGroup_admin.grid',
    export: '/api.informGroup_admin.export',
    get: '/api.informGroup_admin.get',
    create: '/api.informGroup_admin.create',
    update: '/api.informGroup_admin.update',
    delete: '/api.informGroup_admin.delete',
  },

  user: {
    get: '/api.user_user.get',
    update: '/api.user_user.update',
    sendProfile: '/api.user_user.sendProfile',
    signature: '/api.user_user.signature',
    resetPinCode: '/api.user_user.resetPinCode',
    changeEmail: '/api.user_user.changeEmail',
    updateNationalIdentity: '/api.user_user.updateNationalIdentity',
    updateLanguage: '/api.user_user.updateLanguage',
  },

  userSocialProfile: {
    updateNickname: '/api.socialProfile_user.UpdateSocialProfile',
    updateAvatar: '/api.socialProfile_user.UploadAvatar',
    getProfileSummary: '/api.socialProfile_user.GetProfileSummary',
    updateUserDescription: '/api.socialProfile_user.UpdateDescription',
    downloadAvatar: '/api.socialProfile_user.DownloadAvatar',
  },

  copyTradingHistory: {
    grid: '/api.copyTradingHistory_user.grid',
  },

  copyTradingHistoryAdmin: {
    grid: '/api.copyTradingHistory_admin.grid',
    export: '/api.copyTradingHistory_admin.export',
  },

  userDocument: {
    get: '/api.userDocument_user.get',
    getAll: '/api.userDocument_user.grid',
    upload: '/api.userDocument_user.upload',
    delete: '/api.userDocument_user.delete',
  },

  userAdmin: {
    grid: `/api.${USER_ADMIN}.grid`,
    gridUnmasked: `/api.${USER_ADMIN}.gridUnmasked`,
    gridTotals: `/api.${USER_ADMIN}.gridTotals`,
    create: `/api.${USER_ADMIN}.create`,
    update: `/api.${USER_ADMIN}.update`,
    updateUnmasked: `/api.${USER_ADMIN}.${UPDATE_UNMASKED}`,
    delete: `/api.${USER_ADMIN}.delete`,
    restore: `/api.${USER_ADMIN}.restore`,
    get: `/api.${USER_ADMIN}.get`,
    getUnmasked: `/api.${USER_ADMIN}.${GET_UNMASKED}`,
    setPassword: `/api.${USER_ADMIN}.setPassword`,
    signInAs: `/api.${USER_ADMIN}.signInAs`,
    resetPinCode: `/api.${USER_ADMIN}.resetPinCode`,
    enable: `/api.${USER_ADMIN}.enable`,
    promote: `/api.${USER_ADMIN}.promote`,
    getDeclineReasonText: `/api.${USER_ADMIN}.getDeclineReasonText`,
    export: `/api.${USER_ADMIN}.export`,
    exportUnmasked: `/api.${USER_ADMIN}.exportUnmasked`,
    setParentIb: `/api.${USER_ADMIN}.setParentIb`,
    setAECode: `/api.${USER_ADMIN}.setAECode`,
    setBDMCode: `/api.${USER_ADMIN}.setBDMCode`,
    removeAECode: `/api.${USER_ADMIN}.removeAECode`,
    addComment: `/api.${USER_ADMIN}.addComment`,
    changeClientSourceType: `/api.${USER_ADMIN}.changeClientSourceType`,
    changeEmail: `/api.${USER_ADMIN}.changeEmail`,
    changeEmailForcibly: `/api.${USER_ADMIN}.changeEmailForcibly`,
    changeNickname: `/api.${USER_ADMIN}.changeNickname`,
    followUp: `/api.${USER_ADMIN}.followUp`,
    generatePinCode: `/api.${USER_ADMIN}.generatePinCode`,
    resetPassword: `/api.${USER_ADMIN}.resetPassword`,
    setMam: `/api.${USER_ADMIN}.setMam`,
    setIb: `/api.${USER_ADMIN}.setIb`,
    search: `/api.${USER_ADMIN}.search`,
    sendToVerification: `/api.${USER_ADMIN}.sendToVerification`,
    syncToCrm: `/api.${USER_ADMIN}.syncToCrm`,
  },

  userTradingSettings: {
    get: '/api.userTradingSettings_admin.get',
    update: '/api.userTradingSettings_admin.update',
  },

  userWalletCategorySettings: {
    get: '/api.userWalletCategorySettings_admin.get',
    update: '/api.userWalletCategorySettings_admin.update',
  },

  userAccountArchivingSettings: {
    get: '/api.userAccountArchivingSettings_admin.get',
    update: '/api.userAccountArchivingSettings_admin.update',
  },

  userSettings: {
    get: '/api.userSettings_admin.get',
    update: '/api.userSettings_admin.update',
  },

  userStaffSettingsSettings: {
    get: '/api.userStaffSettings_admin.get',
    update: '/api.userStaffSettings_admin.update',
  },

  userTradingAccountSettings: {
    get: '/api.tradingAccountSettings.get',
    getAccountCreationSettings: '/api.tradingAccountSettings.GetAccountCreationSettings',
  },

  userTradingAccountTypeLimitAdmin: {
    grid: '/api.userTradingAccountTypeLimit_admin.grid',
    export: '/api.userTradingAccountTypeLimit_admin.export',
    get: '/api.userTradingAccountTypeLimit_admin.get',
    create: '/api.userTradingAccountTypeLimit_admin.create',
    update: '/api.userTradingAccountTypeLimit_admin.update',
    delete: '/api.userTradingAccountTypeLimit_admin.delete',
  },

  userTradingAccountType: {
    grid: '/api.userTradingAccountType_admin.grid',
    get: '/api.userTradingAccountType_admin.get',
    create: '/api.userTradingAccountType_admin.create',
    update: '/api.userTradingAccountType_admin.update',
    delete: '/api.userTradingAccountType_admin.delete',
  },

  userCommissionSettings: {
    get: '/api.userCommissionSettings_admin.get',
    update: '/api.userCommissionSettings_admin.update',
  },

  userOneTimePasswordSettings: {
    get: '/api.userOtpSettings_admin.get',
    update: '/api.userOtpSettings_admin.update',
  },

  tradingAccountSettings: {
    get: '/api.tradingAccountSettings_admin.get',
    update: '/api.tradingAccountSettings_admin.update',
  },

  tradingAccountCommonSettings: {
    get: '/api.tradingAccountCommonSettings_admin.get',
    update: '/api.tradingAccountCommonSettings_admin.update',
  },

  tradingAccountTypeCommonSettings: {
    get: '/api.tradingAccountTypeCommonSettings_admin.get',
    update: '/api.tradingAccountTypeCommonSettings_admin.update',
  },

  tradingAccountTypeSettings: {
    get: '/api.tradingAccountTypeSettings_admin.get',
    update: '/api.tradingAccountTypeSettings_admin.update',
  },

  tradingPlatformBrandingSettingsAdmin: {
    grid: '/api.tradingPlatformBrandingSettings_admin.grid',
    export: '/api.tradingPlatformBrandingSettings_admin.export',
    get: '/api.tradingPlatformBrandingSettings_admin.get',
    getInfo: '/api.tradingPlatformBrandingSettings_admin.getInfo',
    delete: '/api.tradingPlatformBrandingSettings_admin.delete',
    update: '/api.tradingPlatformBrandingSettings_admin.update',
    create: '/api.tradingPlatformBrandingSettings_admin.create',
    getI18N: '/api.tradingPlatformBrandingSettings_admin.getI18N',
    updateI18N: '/api.tradingPlatformBrandingSettings_admin.updateI18N',
    enable: '/api.tradingPlatformBrandingSettings_admin.enable',
  },

  tradingPlatformBrandingSettingsUser: {
    getList: '/api.tradingPlatformBrandingSettings_user.getList',
  },

  userDocumentAdmin: {
    get: '/api.userDocument_admin.get',
    grid: '/api.userDocument_admin.grid',
    promote: '/api.userDocument_admin.promote',
    delete: '/api.userDocument_admin.delete',
    getDetails: '/api.userDocument_admin.getDetails',
    upload: '/api.userDocument_admin.upload',
    update: '/api.userDocument_admin.update',
  },

  mamMasterAccountAdmin: {
    grid: '/api.mamMasterAccount_admin.grid',
    export: '/api.mamMasterAccount_admin.export',
    get: '/api.mamMasterAccount_admin.get',
    getInfo: '/api.mamMasterAccount_admin.getInfo',
    update: '/api.mamMasterAccount_admin.update',
    promote: '/api.mamMasterAccount_admin.promote',
    addComment: '/api.mamMasterAccount_admin.addComment',
    getCommissionAccountInfo: '/api.mamMasterAccount_admin.getCommissionAccountInfo',
  },

  mamInvestorAccountAdmin: {
    grid: '/api.mamInvestorAccount_admin.grid',
    export: '/api.mamInvestorAccount_admin.export',
  },

  ibrelation: {
    getIbSettings: '/api.ibrelation_admin.getIbSettings',
    setAsMainIb: '/api.ibrelation_admin.setAsMainIb',
    searchIb: '/api.ibrelation_admin.searchIb',
    getIbDetails: '/api.ibrelation_admin.getIbDetails',
    update: '/api.ibrelation_admin.update',
    exportIbChildren: '/api.ibrelation_admin.exportIbChildren',
  },

  ibrelationUser: {
    getAccounts: '/api.IbRelation_user.getAccounts',
    getIbInfo: '/api.IbRelation_user.getIbInfo',
  },

  partnershipAdmin: {
    getIbChildren: '/api.partnership_admin.getIbChildren',
    exportIbChildren: '/api.partnership_admin.exportIbChildren',
    getIbTree: '/api.partnership_admin.getIbTree',
  },

  partnershipUser: {
    getIbTraders: '/api.partnership_user.getIbTraders',
    exportIbTraders: '/api.partnership_user.exportIbTraders',
    getDirectClients: '/api.partnership_user.getDirectClients',
    exportDirectClients: '/api.partnership_user.exportDirectClients',
    getIbTree: '/api.partnership_user.getIbTree',
  },

  reportUser: {
    grid: '/api.report_user.grid',
    download: '/api.report_user.download',
  },

  closedReportAdmin: {
    grid: '/api.closedReport_admin.grid',
    export: '/api.closedReport_admin.export',
  },

  closedReportUser: {
    grid: '/api.closedReport_user.grid',
    export: '/api.closedReport_user.export',
  },

  volumeReportAdmin: {
    grid: '/api.volumeReport_admin.grid',
    total: '/api.volumeReport_admin.totals',
    export: '/api.volumeReport_admin.export',
    salesUserGrid: '/api.volumeReport_admin.salesUserGrid',
    salesUserExport: '/api.volumeReport_admin.salesUserExport',
    salesUserTotal: '/api.volumeReport_admin.salesUserTotals',
    businessManagerGrid: '/api.volumeReport_admin.businessManagerGrid',
    businessManagerExport: '/api.volumeReport_admin.businessManagerExport',
    businessManagerTotal: '/api.volumeReport_admin.businessManagerTotals',
  },

  volumeReportUser: {
    grid: '/api.volumeReport_user.grid',
    export: '/api.volumeReport_user.export',
    total: '/api.volumeReport_user.totals',
    salesUserGrid: '/api.volumeReport_user.salesUserGrid',
    salesUserExport: '/api.volumeReport_user.salesUserExport',
    salesUserTotal: '/api.volumeReport_user.salesUserTotals',
    businessManagerGrid: '/api.volumeReport_user.businessManagerGrid',
    businessManagerExport: '/api.volumeReport_user.businessManagerExport',
    businessManagerTotal: '/api.volumeReport_user.businessManagerTotals',
  },

  qFormUser: {
    createTerminationRequest: '/api.qForm_user.CreateTerminationRequest',
    getSingleQForm: '/api.qForm_user.getSingleQForm',
    getQForm: '/api.qForm_user.GetQForm',
    getAll: '/api.qForm_user.getAll',
    create: '/api.qForm_user.create',
    get: '/api.qForm_user.get',
    update: '/api.qForm_user.update',
    send: '/api.qForm_user.saveAndSend',
    delete: '/api.qForm_user.delete',
    getMarkUp: '/api.qForm_user.getMarkUp',
    getInfo: '/api.qForm_user.getInfo',
  },

  qFormAdmin: {
    grid: '/api.qForm_admin.grid',
    get: '/api.qForm_admin.get',
    update: '/api.qForm_admin.update',
    delete: '/api.qForm_admin.delete',
    promote: '/api.qForm_admin.promote',
    export: '/api.qForm_admin.export',
    addComment: '/api.qForm_admin.addComment',
  },

  imageProcessingAdmin: {
    startDocumentsProcessingJob: '/api.imageProcessing_admin.startDocumentsProcessingJob',
    startAvatarsProcessingJob: '/api.imageProcessing_admin.startAvatarsProcessingJob',
  },

  wallet: {
    getWallets: '/api.wallet_user.getWallets',
    getWalletsAccount: '/api.wallet_user.getWalletsAccount',
    getTransitWalletAccounts: '/api.wallet_user.getTransitWalletAccounts',
  },

  walletAdmin: {
    internalTransfer: '/api.wallet_admin.internaltransfer',
    grid: '/api.wallet_admin.grid',
    gridWithStaff: `/api.wallet_admin.${GRID_WITH_STAFF}`,
    export: '/api.wallet_admin.export',
    exportWithStaff: `/api.wallet_admin.${EXPORT_WITH_STAFF}`,
    get: '/api.wallet_admin.get',
    getWithStaff: `/api.wallet_admin.${GET_WITH_STAFF}`,
    gridTotals: '/api.wallet_admin.gridTotals',
    gridWithStaffTotals: `/api.wallet_admin.${GRID_WITH_STAFF_TOTALS}`,
  },

  walletOperation: {
    get: '/api.walletOperation_user.get',
    grid: '/api.walletOperation_user.grid',
    export: '/api.walletOperation_user.export',
    totals: '/api.walletOperation_user.getTotals',
  },

  ibWalletOperation: {
    get: '/api.ibWalletOperation_user.get',
    grid: '/api.ibWalletOperation_user.grid',
    export: '/api.ibWalletOperation_user.export',
    totals: '/api.ibWalletOperation_user.getTotals',
  },

  walletOperationAdmin: {
    get: `/api.${WALLET_OPERATION_ADMIN}.get`,
    getWithStaff: `/api.${WALLET_OPERATION_ADMIN}.${GET_WITH_STAFF}`,
    grid: `/api.${WALLET_OPERATION_ADMIN}.grid`,
    gridWithStaff: `/api.${WALLET_OPERATION_ADMIN}.${GRID_WITH_STAFF}`,
    export: `/api.${WALLET_OPERATION_ADMIN}.export`,
    exportWithStaff: `/api.${WALLET_OPERATION_ADMIN}.${EXPORT_WITH_STAFF}`,
    create: `/api.${WALLET_OPERATION_ADMIN}.create`,
    bulkcreate: `/api.${WALLET_OPERATION_ADMIN}.bulkcreate`,
  },

  walletCategoryAdmin: {
    grid: '/api.walletCategory_admin.grid',
    zeroBalance: '/api.walletCategory_admin.zeroBalance',
  },

  tradingAccountTypeUser: {
    getSelfTradingTypes: '/api.tradingAccountType_user.getSelfTradingTypes',
  },

  tradingAccountTypeAdmin: {
    grid: '/api.tradingAccountType_admin.grid',
    export: '/api.tradingAccountType_admin.export',
    getList: '/api.tradingAccountType_admin.getList',
    get: '/api.tradingAccountType_admin.get',
    getInfo: '/api.tradingAccountType_admin.getInfo',
    create: '/api.tradingAccountType_admin.create',
    clone: '/api.tradingAccountType_admin.clone',
    update: '/api.tradingAccountType_admin.update',
    delete: '/api.tradingAccountType_admin.delete',
    archive: '/api.tradingAccountType_admin.archive',
    getI18N: '/api.tradingAccountType_admin.getI18N',
    updateI18N: '/api.tradingAccountType_admin.updateI18N',
  },

  tradingGroupAdmin: {
    grid: '/api.tradingGroup_admin.grid',
    export: '/api.tradingGroup_admin.export',
    get: '/api.tradingGroup_admin.get',
    getInfo: '/api.tradingGroup_admin.getInfo',
    create: '/api.tradingGroup_admin.create',
    clone: '/api.tradingGroup_admin.clone',
    update: '/api.tradingGroup_admin.update',
    delete: '/api.tradingGroup_admin.delete',
    archive: '/api.tradingGroup_admin.archive',
    getList: '/api.tradingGroup_admin.getList',
    search: '/api.tradingGroup_admin.search',
  },

  tradingSymbolAdmin: {
    grid: '/api.tradingSymbol_admin.grid',
    export: '/api.tradingSymbol_admin.export',
    get: '/api.tradingSymbol_admin.get',
    getInfo: '/api.tradingSymbol_admin.getInfo',
    create: '/api.tradingSymbol_admin.create',
    clone: '/api.tradingSymbol_admin.clone',
    update: '/api.tradingSymbol_admin.update',
    delete: '/api.tradingSymbol_admin.delete',
    getList: '/api.tradingSymbol_admin.getList',
    preImport: '/api.tradingSymbol_admin.preImport',
    import: '/api.tradingSymbol_admin.import',
  },

  tradingSymbolGroupAdmin: {
    grid: '/api.tradingSymbolGroup_admin.grid',
    export: '/api.tradingSymbolGroup_admin.export',
    get: '/api.tradingSymbolGroup_admin.get',
    create: '/api.tradingSymbolGroup_admin.create',
    update: '/api.tradingSymbolGroup_admin.update',
    delete: '/api.tradingSymbolGroup_admin.delete',
  },

  shortLinkTradingGroupAdmin: {
    grid: '/api.shortLinkTradingGroup_admin.grid',
    export: '/api.shortLinkTradingGroup_admin.export',
    get: '/api.shortLinkTradingGroup_admin.get',
  },

  initTradingAccountTypeSettingsAdmin: {
    get: '/api.initTradingAccountTypeSettings_admin.get',
    update: '/api.initTradingAccountTypeSettings_admin.update',
  },

  tradingAccountAdmin: {
    grid: `/api.${TRADING_ACCOUNT_ADMIN}.grid`,
    gridWithStaff: `/api.${TRADING_ACCOUNT_ADMIN}.${GRID_WITH_STAFF}`,
    gridTotals: `/api.${TRADING_ACCOUNT_ADMIN}.gridTotals`,
    gridWithStaffTotals: `/api.${TRADING_ACCOUNT_ADMIN}.${GRID_WITH_STAFF_TOTALS}`,
    export: `/api.${TRADING_ACCOUNT_ADMIN}.export`,
    exportWithStaff: `/api.${TRADING_ACCOUNT_ADMIN}.${EXPORT_WITH_STAFF}`,
    enable: `/api.${TRADING_ACCOUNT_ADMIN}.enable`,
    show: `/api.${TRADING_ACCOUNT_ADMIN}.show`,
    archive: `/api.${TRADING_ACCOUNT_ADMIN}.archive`,
    get: `/api.${TRADING_ACCOUNT_ADMIN}.get`,
    getWithStaff: `/api.${TRADING_ACCOUNT_ADMIN}.${GET_WITH_STAFF}`,
    create: `/api.${TRADING_ACCOUNT_ADMIN}.create`,
    linkExistingAccount: `/api.${TRADING_ACCOUNT_ADMIN}.linkExistingAccount`,
    getAccountWalletOperations: `/api.${TRADING_ACCOUNT_ADMIN}.getAccountWalletOperations`,
    getList: `/api.${TRADING_ACCOUNT_ADMIN}.getList`,
    getInternalTransferInfo: `/api.${TRADING_ACCOUNT_ADMIN}.getInternalTransferInfo`,
    setIb: `/api.${TRADING_ACCOUNT_ADMIN}.setIb`,
    sync: `/api.${TRADING_ACCOUNT_ADMIN}.sync`,
    push: `/api.${TRADING_ACCOUNT_ADMIN}.push`,
    setAccountType: `/api.${TRADING_ACCOUNT_ADMIN}.setAccountType`,
    changeLeverage: `/api.${TRADING_ACCOUNT_ADMIN}.changeLeverage`,
    changeGroup: `/api.${TRADING_ACCOUNT_ADMIN}.changeGroup`,
    syncToCrm: `/api.${TRADING_ACCOUNT_ADMIN}.syncToCrm`,
    getBonusStopTrigger: `/api.${TRADING_ACCOUNT_ADMIN}.getBonusStopTrigger`,
    updateBonusStopTrigger: `/api.${TRADING_ACCOUNT_ADMIN}.updateBonusStopTrigger`,
  },

  commissionHistoryAdmin: {
    grid: '/api.commissionHistory_admin.grid',
    export: '/api.commissionHistory_admin.export',
    get: '/api.commissionHistory_admin.get',
  },

  commissionHistoryPerIbAdmin: {
    grid: '/api.commissionHistoryPerIb_admin.grid',
  },

  tradingAccountTypeCategoryAdmin: {
    grid: '/api.tradingAccountTypeCategory_admin.grid',
    export: '/api.tradingAccountTypeCategory_admin.export',
    get: '/api.tradingAccountTypeCategory_admin.get',
    getInfo: '/api.tradingAccountTypeCategory_admin.getInfo',
    create: '/api.tradingAccountTypeCategory_admin.create',
    update: '/api.tradingAccountTypeCategory_admin.update',
    clone: '/api.tradingAccountTypeCategory_admin.clone',
    delete: '/api.tradingAccountTypeCategory_admin.delete',
    archive: '/api.tradingAccountTypeCategory_admin.archive',
    show: '/api.tradingAccountTypeCategory_admin.show',
    getI18N: '/api.tradingAccountTypeCategory_admin.getI18N',
    updateI18N: '/api.tradingAccountTypeCategory_admin.updateI18N',
    getList: '/api.tradingAccountTypeCategory_admin.getList',
    getCommissionList: '/api.tradingAccountTypeCategory_admin.getCommissionList',
  },

  tradingAccountTypeCategoryUser: {
    getList: '/api.tradingAccountTypeCategory_user.getList',
    getCommissionList: '/api.tradingAccountTypeCategory_user.getCommissionList',
  },

  userTradingAccountTypeCategoryAdmin: {
    grid: '/api.userTradingAccountTypeCategory_admin.grid',
    update: '/api.userTradingAccountTypeCategory_admin.update',
    delete: '/api.userTradingAccountTypeCategory_admin.delete',
    updateIsHidden: '/api.userTradingAccountTypeCategory_admin.updateIsHidden',
    moveTradingGroup: '/api.userTradingAccountTypeCategory_admin.moveTradingGroup',
  },

  userTradingAccountTypeCategoryUser: {
    getIbInfo: '/api.userTradingAccountTypeCategory_user.getIbInfo',
    getSalesInfo: '/api.userTradingAccountTypeCategory_user.getSalesInfo',
    grid: '/api.userTradingAccountTypeCategory_user.grid',
    update: '/api.userTradingAccountTypeCategory_user.update',
  },

  tradePayoutAdmin: {
    grid: '/api.tradePayout_admin.grid',
    checkPeriod: '/api.tradePayout_admin.checkPeriod',
  },

  tradePayoutUserAdmin: {
    get: '/api.tradePayoutUser_admin.get',
    grid: '/api.tradePayoutUser_admin.grid',
    export: '/api.tradePayoutUser_admin.export',
  },

  tradingAccountUser: {
    get: '/api.tradingAccount_user.get',
    editTradingAccountName: '/api.TradingAccount_user.editTradingAccountName',
    getAllAccounts: '/api.tradingAccount_user.getAllAccounts',
    getCopyTradingInvestorAccount: '/api.investorAccount_user.getDetails',
    getCopyTradingInvestorOpenOrders: '/api.investorAccount_user.hasOpenOrders',
    getCopyTradingMasterAccount: '/api.masterAccount_user.getDetails',
    changeInvestorPassword: '/api.tradingAccount_user.changeInvestorPassword',
    changeMasterPassword: '/api.tradingAccount_user.changeMasterPassword',
    changeLeverage: '/api.tradingAccount_user.changeLeverage',
    setAccountNotification: '/api.tradingAccount_user.setAccountNotification',
    archive: '/api.tradingAccount_user.archive',
    refill: '/api.TradingAccount_user.refill',
    internalTransfer: '/api.tradingAccount_user.internalTransfer',
    internalTransferInfo: '/api.tradingAccount_user.getInternalTransferInfo',
    sync: '/api.tradingAccount_user.sync',
    syncAll: '/api.tradingAccount_user.syncAll',
    getAccountsBalance: '/api.tradingAccount_user.getAccountsBalance',
    masterAccountCreate: '/api.masterAccount_user.create',
    investorAccountCreate: '/api.investorAccount_user.create',
    selfTradeAccountCreate: '/api.tradingAccount_user.create',
    getInvestorProfitabilityChart: '/api.investorAccount_user.getProfitabilityChart',
    getMasterProfitabilityChart: '/api.masterAccount_user.getProfitabilityChart',
    updateCopyTradingInvestorAccount: '/api.investorAccount_user.update',
    updateCopyTradingMasterAccount: '/api.masterAccount_user.update',
    getMyStatisticsMasters: '/api.masterAccount_user.getMyStatisticsMasters',
    getInvestorAccounts: '/api.investorAccount_user.getInvestors',
    getInvestorProfileOperationHistory: '/api.investorProfile_user.getOperationHistory',
    getInvestorProfile: '/api.investorProfile_user.getProfile',
  },

  tradingAccountCardUser: {
    getSelfTradingAccounts: '/api.tradingAccountCard_user.getSelfTradingAccounts',
    getActions: '/api.tradingAccountCard_user.getActions',
    getCategories: '/api.tradingAccountCard_user.getCategories',
  },

  copyTradingAccountCardUser: {
    getInvestorAccounts: '/api.copyTradingAccountCard_user.getInvestorAccounts',
    getMasterAccounts: '/api.copyTradingAccountCard_user.getMasterAccounts',
  },

  tradingHistoryUser: {
    grid: '/api.tradingHistory_user.grid',
    export: '/api.tradingHistory_user.export',
  },

  tradingHistoryAdmin: {
    grid: '/api.tradingHistory_admin.grid',
    export: '/api.tradingHistory_admin.export',
  },

  issueUser: {
    grid: '/api.issue_user.grid',
    get: '/api.issue_user.get',
    create: '/api.issue_user.create',
  },

  issueAdmin: {
    grid: '/api.issue_admin.grid',
    export: '/api.issue_admin.export',
    get: '/api.issue_admin.get',
    update: '/api.issue_admin.update',
    promote: '/api.issue_admin.promote',
    syncToCrm: '/api.issue_admin.syncToCrm',
    delete: '/api.issue_admin.delete',
    getSupportUsers: '/api.issue_admin.getSupportUsers',
  },

  notification: {
    grid: '/api.notification_user.grid',
    get: '/api.notification_user.get',
    delete: '/api.notification_user.delete',
    markAs: '/api.notification_user.markAs',
    getEventTypes: '/api.notification_user.getEventTypes',
    accept: '/api.notification_user.accept',
    reject: '/api.notification_user.reject',
  },

  notificationTemplateAdmin: {
    grid: '/api.notificationTemplate_admin.grid',
    export: '/api.notificationTemplate_admin.export',
    get: '/api.notificationTemplate_admin.get',
    getInfo: '/api.notificationTemplate_admin.getInfo',
    enable: '/api.notificationTemplate_admin.enable',
    update: '/api.notificationTemplate_admin.update',
    getMacros: '/api.notificationTemplate_admin.getMacros',
    getI18N: '/api.notificationTemplate_admin.getI18N',
    updateI18N: '/api.notificationTemplate_admin.updateI18N',
  },

  shortLinkAdmin: {
    grid: '/api.shortLink_admin.grid',
    export: '/api.shortLink_admin.export',
    promote: '/api.shortLink_admin.promote',
    addComment: '/api.shortLink_admin.addComment',
    get: '/api.shortLink_admin.get',
    getInfo: '/api.shortLink_admin.getInfo',
    create: '/api.shortLink_admin.create',
    update: '/api.shortLink_admin.update',
    delete: '/api.shortLink_admin.delete',
    show: '/api.shortLink_admin.show',
    getSettings: '/api.shortLink_admin.getSettings',
    setSettings: '/api.shortLink_admin.setSettings',
    moveTradingGroup: '/api.shortLink_admin.moveTradingGroup',
  },

  shortLinkUser: {
    grid: '/api.shortLink_user.grid',
    get: '/api.shortLink_user.get',
    toReview: '/api.shortLink_user.toReview',
    create: '/api.shortLink_user.create',
    hide: '/api.shortLink_user.hide',
    delete: '/api.shortLink_user.delete',
    setName: '/api.shortLink_user.setName',
    setSettings: '/api.shortLink_user.setSettings',
  },

  componentAdmin: {
    grid: '/api.component_admin.grid',
    export: '/api.component_admin.export',
    get: '/api.component_admin.get',
    getInfo: '/api.component_admin.getInfo',
    create: '/api.component_admin.create',
    clone: '/api.component_admin.clone',
    update: '/api.component_admin.update',
    delete: '/api.component_admin.delete',
    enable: '/api.component_admin.enable',
    getConfiguration: '/api.component_admin.getConfiguration',
    getConfigurationUnmasked: '/api.component_admin.getConfigurationUnmasked',
    getSystemConfiguration: '/api.component_admin.getSystemConfiguration',
    setConfiguration: '/api.component_admin.setConfiguration',
    setConfigurationUnmasked: '/api.component_admin.setConfigurationUnmasked',
    setSystemConfiguration: '/api.component_admin.setSystemConfiguration',
    getDefaultConfiguration: '/api.component_admin.getDefaultConfiguration',
    getAllInterfaces: '/api.component_admin.getAllInterfaces',
    getClasses: '/api.component_admin.getClasses',
    getList: '/api.component_admin.getList',
    getClassList: '/api.component_admin.getClassList',
  },

  psp: {
    getAll: '/api.psp_user.grid',
    get: '/api.psp_user.get',
    getPlugins: '/api.psp_user.getPlugins',
    getInfo: '/api.psp_user.getInfo',
    deposit: '/api.psp_user.deposit',
    withdraw: '/api.psp_user.withdrawal',
    export: '/api.psp_user.export',
    recovery: '/api.psp_user.recovery',
    cancel: '/api.psp_user.cancel',
    attachFile: '/api.psp_user.attachFile',
    download: '/api.psp_user.download',
    isBeneficiaryBankNameValid: '/api.psp_user.isBeneficiaryBankNameValid',
  },

  userBankAccountUser: {
    create: '/api.userBankAccount_user.create',
    update: '/api.userBankAccount_user.update',
    get: '/api.userBankAccount_user.get',
    delete: '/api.userBankAccount_user.delete',
    reuploadDocument: '/api.userBankAccount_user.reuploaddocument',
    download: '/api.userBankAccount_user.download',
    grid: '/api.userBankAccount_user.grid',
    getDetails: '/api.userBankAccount_user.getDetails',
    reuploadBankStatementForAccount: '/api.userBankAccount_user.reuploadBankStatementForAccount',
    getBanks: '/api.bank_user.getBanks',
    setPreferred: '/api.userBankAccount_user.setpreferred',
  },

  cryptoWalletAccountUser: {
    setPreferred: '/api.userCryptoWalletAccount_user.setpreferred',
    grid: '/api.userCryptoWalletAccount_user.grid',
    getDetails: '/api.userCryptoWalletAccount_user.getDetails',
    update: '/api.userCryptoWalletAccount_user.update',
    delete: '/api.userCryptoWalletAccount_user.delete',
    create: '/api.userCryptoWalletAccount_user.create',
  },

  cryptoWalletAccountAdmin: {
    grid: '/api.userCryptoWalletAccount_admin.grid',
    export: '/api.userCryptoWalletAccount_admin.export',
    create: '/api.userCryptoWalletAccount_admin.create',
    get: '/api.userCryptoWalletAccount_admin.get',
    getInfo: '/api.userCryptoWalletAccount_admin.getInfo',
    update: '/api.userCryptoWalletAccount_admin.update',
    delete: '/api.userCryptoWalletAccount_admin.delete',
    addComment: '/api.userCryptoWalletAccount_admin.addComment',
    show: '/api.userCryptoWalletAccount_admin.show',
    promote: '/api.userCryptoWalletAccount_admin.promote',
    downloadWalletOwnership: '/api.userCryptoWalletAccount_admin.downloadWalletOwnership',
    downloadReceivingAddress: '/api.userCryptoWalletAccount_admin.downloadReceivingAddress',
    getDeclineReasonText: '/api.userCryptoWalletAccount_admin.getDeclineReasonText',
  },

  userDigitalWalletUser: {
    get: '/api.userDigitalWallet_user.get',
    create: '/api.userDigitalWallet_user.create',
    reuploadDocument: '/api.userDigitalWallet_user.reuploaddocument',
    download: '/api.userDigitalWallet_user.download',
  },

  userCardAccountUser: {
    get: '/api.usercard_user.get',
    create: '/api.usercard_user.create',
    reuploadDocument: '/api.usercard_user.reuploaddocuments',
    download: '/api.usercard_user.download',
  },

  userNameAliasUser: {
    create: '/api.userNameAlias_user.create',
    attach: '/api.userNameAlias_user.attachToBank',
  },

  userNameAliasAdmin: {
    grid: '/api.userNameAlias_admin.grid',
    export: '/api.userNameAlias_admin.export',
    get: '/api.userNameAlias_admin.get',
    getInfo: '/api.userNameAlias_admin.getInfo',
    create: '/api.userNameAlias_admin.create',
    update: '/api.userNameAlias_admin.update',
    delete: '/api.userNameAlias_admin.delete',
    addComment: '/api.userNameAlias_admin.addComment',
    promote: '/api.userNameAlias_admin.promote',
    getDeclineReasonText: '/api.userNameAlias_admin.getDeclineReasonText',
  },

  localDeposit: {
    get: '/api.localdeposit.get',
    reject: '/api.localdeposit.reject',
    submit: '/api.localdeposit.submit',
  },

  fileItem: {
    upload: '/api.fileItem.upload',
  },

  imageAdmin: {
    grid: '/api.image_admin.grid',
    export: '/api.image_admin.export',
    get: '/api.image_admin.get',
    getInfo: '/api.image_admin.getInfo',
    create: '/api.image_admin.create',
    update: '/api.image_admin.update',
    delete: '/api.image_admin.delete',
    download: '/api.image_admin.download',
  },

  imageUser: {
    download: '/api.image_user.download',
  },

  pspAdmin: {
    grid: `/api.${PSP_ADMIN}.grid`,
    gridTotals: `/api.${PSP_ADMIN}.gridTotals`,
    gridWithStaff: `/api.${PSP_ADMIN}.${GRID_WITH_STAFF}`,
    gridWithStaffTotals: `/api.${PSP_ADMIN}.gridWithStaffTotals`,
    get: `/api.${PSP_ADMIN}.get`,
    getWithStaff: `/api.${PSP_ADMIN}.${GET_WITH_STAFF}`,
    getPromoteActions: `/api.${PSP_ADMIN}.getPromoteActions`,
    depositCommit: `/api.${PSP_ADMIN}.depositCommit`,
    withdrawalCommit: `/api.${PSP_ADMIN}.withdrawalCommit`,
    approve: `/api.${PSP_ADMIN}.approve`,
    reject: `/api.${PSP_ADMIN}.reject`,
    refund: `/api.${PSP_ADMIN}.refund`,
    addComment: `/api.${PSP_ADMIN}.addComment`,
    export: `/api.${PSP_ADMIN}.export`,
    exportWithStaff: `/api.${PSP_ADMIN}.${EXPORT_WITH_STAFF}`,
    fixStatus: `/api.${PSP_ADMIN}.fixStatus`,
    getAllowedStatusFixes: `/api.${PSP_ADMIN}.getAllowedStatusFixes`,
    recovery: `/api.${PSP_ADMIN}.recovery`,
    getDeclineReasonText: `/api.${PSP_ADMIN}.getDeclineReasonText`,
    getWithdrawalDeclineReasonText: `/api.${PSP_ADMIN}.getWithdrawalDeclineReasonText`,
    syncToCrm: `/api.${PSP_ADMIN}.syncToCrm`,
    reprocessWithdrawal: `/api.${PSP_ADMIN}.reprocessWithdrawal`,

    getFollowUpText: '/api.psp_admin.getFollowUpText',
    followUp: '/api.psp_admin.followUp',
  },

  pspOperationFileItemAdmin: {
    grid: '/api.pspOperationFileItem_admin.grid',
    create: '/api.pspOperationFileItem_admin.create',
    delete: '/api.pspOperationFileItem_admin.delete',
    download: '/api.pspOperationFileItem_admin.download',
  },

  pspOperationReportAdmin: {
    grid: '/api.pspOperationReport_admin.grid',
    export: '/api.pspOperationReport_admin.export',
    totalAmount: '/api.pspOperationReport_admin.gridTotals',
  },

  pspOperationPerCountryReportAdmin: {
    grid: '/api.pspOperationPerCountryReport_admin.grid',
    export: '/api.pspOperationPerCountryReport_admin.export',
    totalAmount: '/api.pspOperationPerCountryReport_admin.gridTotals',
  },

  pspOperationHistoryReportAdmin: {
    grid: '/api.pspOperationHistoryReport_admin.grid',
    export: '/api.pspOperationHistoryReport_admin.export',
  },

  pspOperationStatisticsReportAdmin: {
    grid: '/api.pspOperationStatisticsReport_admin.grid',
    export: '/api.pspOperationStatisticsReport_admin.export',
    gridTotals: '/api.pspOperationStatisticsReport_admin.gridTotals',
  },

  pspActivityTimeReportAdmin: {
    grid: '/api.pspActivityTimeReport_admin.grid',
    export: '/api.pspActivityTimeReport_admin.export',
  },

  paymentSummaryAdmin: {
    grid: '/api.paymentSummary_admin.grid',
    export: '/api.paymentSummary_admin.export',
  },

  userAccountsAdmin: {
    getAccountsSummary: '/api.userAccounts_admin.getAccountsSummary',
  },

  oneTimeLink: {
    validatePinCode: '/api.oneTimeLink.validatePinCodeLink',
    validatePasswordRecovery: '/api.oneTimeLink.validatePasswordRecoveryLink',
    validateConfirmEmailChangeLink: '/api.oneTimeLink.validateConfirmEmailChangeLink',
  },

  tradingBonusAdmin: {
    grid: '/api.tradingBonus_admin.grid',
    export: '/api.tradingBonus_admin.export',
    get: '/api.tradingBonus_admin.get',
    getInfo: '/api.tradingBonus_admin.getInfo',
    create: '/api.tradingBonus_admin.create',
  },

  tradingBonusSummaryAdmin: {
    grid: '/api.tradingBonusSummary_admin.grid',
  },

  tradingBonusUser: {
    grid: '/api.tradingBonus_user.grid',
    export: '/api.tradingBonus_user.export',
  },

  tradingBonusSummaryUser: {
    grid: '/api.tradingBonusSummary_user.grid',
  },

  bonusProposalUser: {
    grid: '/api.userBonusProposal_user.grid',
    accept: '/api.userBonusProposal_user.accept',
  },

  tradingBonusLinkAdmin: {
    addComment: '/api.tradingBonusLink_admin.addComment',
    grid: '/api.tradingBonusLink_admin.grid',
    export: '/api.tradingBonusLink_admin.export',
    get: '/api.tradingBonusLink_admin.get',
    create: '/api.tradingBonusLink_admin.create',
    update: '/api.tradingBonusLink_admin.update',
    delete: '/api.tradingBonusLink_admin.delete',
    promote: '/api.tradingBonusLink_admin.promote',
  },

  tradingBonusLinkReportAdmin: {
    grid: '/api.tradingBonusLinkReport_admin.grid',
    export: '/api.tradingBonusLinkReport_admin.export',
  },

  tradingBonusLinkUser: {
    getInfo: '/api.tradingBonusLink_user.info',
  },

  branding: {
    get: '/HttpHandlers/BrandingHandler.ashx',
  },

  culture: {
    setCulture: '/api.culture.setculture',
  },

  reportAdmin: {
    grid: '/api.report_admin.grid',
    get: '/api.report_admin.get',
    create: '/api.report_admin.create',
    download: '/api.report_admin.download',
    creatableReportTypes: '/api.report_admin.getsupportedreporttypes',
    reportParametersInfo: '/api.report_admin.getreportparametersinfo',
  },

  availableCountriesAdmin: {
    grid: '/api.availableCountries_admin.grid',
    create: '/api.availableCountries_admin.create',
    delete: '/api.availableCountries_admin.delete',
  },

  notesAdmin: {
    grid: '/api.notes_admin.grid',
    export: '/api.notes_admin.export',
    get: '/api.notes_admin.get',
    create: '/api.notes_admin.create',
  },

  autoAssignmentRulesAdmin: {
    grid: '/api.autoAssignmentRule_admin.grid',
    getList: '/api.autoAssignmentRule_admin.getList',
    get: '/api.autoAssignmentRule_admin.get',
    create: '/api.autoAssignmentRule_admin.create',
    update: '/api.autoAssignmentRule_admin.update',
    delete: '/api.autoAssignmentRule_admin.delete',
    enable: '/api.autoAssignmentRule_admin.enable',
  },

  assignmentSettingsAdmin: {
    assignSalesUser: '/api.assignmentSettings_admin.assignSalesUser',
    removeSalesUser: '/api.assignmentSettings_admin.removeSalesUser',
    reassignUsers: '/api.assignmentSettings_admin.reassignUsers',
  },

  autoAssignmentRuleSalesUserAdmin: {
    create: '/api.autoAssignmentRuleSalesUser_admin.create',
    delete: '/api.autoAssignmentRuleSalesUser_admin.delete',
  },

  salesUserAdmin: {
    grid: '/api.salesUser_admin.grid',
    export: '/api.salesUser_admin.export',
    get: '/api.salesUser_admin.get',
    getInfo: '/api.salesUser_admin.getInfo',
    create: '/api.salesUser_admin.create',
    update: '/api.salesUser_admin.update',
    delete: '/api.salesUser_admin.delete',
    enable: '/api.salesUser_admin.enable',
    showInfo: '/api.salesUser_admin.showInfo',
    search: '/api.salesUser_admin.search',
  },

  salesUserClients: {
    get: '/api.salesUserClient_user.get',
    grid: '/api.salesUserClient_user.grid',
    gridTotals: '/api.salesUserClient_user.gridTotals',
    export: '/api.salesUserClient_user.export',
  },

  salesUserPspOperations: {
    get: '/api.salesUserPspOperation_user.get',
    grid: '/api.salesUserPspOperation_user.grid',
    gridTotals: '/api.salesUserPspOperation_user.gridTotals',
    export: '/api.salesUserPspOperation_user.export',
  },

  salesUserTradingAccount: {
    get: '/api.salesUserTradingAccount_user.get',
    grid: '/api.salesUserTradingAccount_user.grid',
    export: '/api.salesUserTradingAccount_user.export',
  },

  salesUserIb: {
    get: '/api.salesUserIb_user.get',
    getTree: '/api.salesUserIb_user.getTree',
    search: '/api.salesUserIb_user.search',
  },

  salesUser: {
    getInfo: '/api.salesUser_user.getInfo',
  },

  tradePayoutUserUser: {
    grid: '/api.tradePayoutUser_user.grid',
    gridTotals: '/api.tradePayoutUser_user.gridTotals',
    export: '/api.tradePayoutUser_user.export',
  },

  businessManagerAdmin: {
    grid: '/api.businessManager_admin.grid',
    export: '/api.businessManager_admin.export',
    get: '/api.businessManager_admin.get',
    getInfo: '/api.businessManager_admin.getInfo',
    create: '/api.businessManager_admin.create',
    update: '/api.businessManager_admin.update',
    delete: '/api.businessManager_admin.delete',
    enable: '/api.businessManager_admin.enable',
    showInfo: '/api.businessManager_admin.showInfo',
    search: '/api.businessManager_admin.search',
  },

  loginHistoryAdmin: {
    grid: '/api.loginHistory_admin.grid',
    export: '/api.loginHistory_admin.export',
    get: '/api.loginHistory_admin.get',
    getInfo: '/api.loginHistory_admin.getInfo',
  },

  externalJobAdmin: {
    grid: '/api.externalJob_admin.grid',
    export: '/api.externalJob_admin.export',
    get: '/api.externalJob_admin.get',
    getInfo: '/api.externalJob_admin.getInfo',
    promote: '/api.externalJob_admin.promote',
  },

  mamInvestor: {
    getAccounts: '/api.mamInvestorAccount_user.getAccounts',
    getCreationInfo: '/api.mamInvestorAccount_user.getCreationInfo',
  },

  mamMasterUserUser: {
    getMamInfo: '/api.mamMasterUser_user.getMamInfo',
    mamInvestorAccountsGrid: '/api.mamMasterUser_user.mamInvestorAccountsGrid',
    exportMamInvestorAccounts: '/api.mamMasterUser_user.exportMamInvestorAccounts',
    mamInvestorUsersGrid: '/api.mamMasterUser_user.mamInvestorUsersGrid',
    exportMamInvestorUsers: '/api.mamMasterUser_user.exportMamInvestorUsers',
  },

  mamManager: {
    createMamLink: '/api.mamMasterAccount_user.create',
    getMamLinkCreationSettings: '/api.mamSettings_user.get',
    getMamAccounts: '/api.mamMasterAccount_user.getPossibleMamMasterAccounts',
    getLinks: '/api.mamMasterAccount_user.getLinks',
    getMamLinkDetails: '/api.mamMasterAccount_user.details',
    updateMamLink: '/api.mamMasterAccount_user.update',
    approveMamLink: '/api.mamMasterAccount_user.sendForApproval',
    getCommissionAccountInfo: '/api.mamMasterAccount_user.getCommissionAccountInfo',
    getMamAccountTypes: '/api.mamMasterAccount_user.getAvailableTypesForCreation',
  },

  salesActivityHistoryAdmin: {
    grid: '/api.salesActivityHistory_admin.grid',
    create: '/api.salesActivityHistory_admin.create',
    update: '/api.salesActivityHistory_admin.update',
    get: '/api.salesActivityHistory_admin.get',
    delete: '/api.salesActivityHistory_admin.delete',
    export: '/api.salesActivityHistory_admin.export',
  },

  userPspFeesAdmin: {
    grid: '/api.userPspFee_admin.grid',
    export: '/api.userPspFee_admin.export',
    get: '/api.userPspFee_admin.get',
    create: '/api.userPspFee_admin.create',
    update: '/api.userPspFee_admin.update',
    delete: '/api.userPspFee_admin.delete',
  },

  paymentSystemConsentAdmin: {
    grid: '/api.paymentSystemConsent_admin.grid',
    export: '/api.paymentSystemConsent_admin.export',
    get: '/api.paymentSystemConsent_admin.get',
    getInfo: '/api.paymentSystemConsent_admin.getInfo',
    create: '/api.paymentSystemConsent_admin.create',
    update: '/api.paymentSystemConsent_admin.update',
    delete: '/api.paymentSystemConsent_admin.delete',
    updateI18N: '/api.paymentSystemConsent_admin.updateI18N',
    getI18N: '/api.paymentSystemConsent_admin.getI18N',
    getList: '/api.paymentSystemConsent_admin.getList',
  },

  pspConsentAdmin: {
    grid: '/api.pspConsent_admin.grid',
    export: '/api.pspConsent_admin.export',
    get: '/api.pspConsent_admin.get',
    create: '/api.pspConsent_admin.create',
    update: '/api.pspConsent_admin.update',
    delete: '/api.pspConsent_admin.delete',
  },

  clientSideTranslationAdmin: {
    grid: '/api.clientSideTranslation_admin.grid',
    export: '/api.clientSideTranslation_admin.export',
    get: '/api.clientSideTranslation_admin.get',
    getInfo: '/api.clientSideTranslation_admin.getInfo',
    update: '/api.clientSideTranslation_admin.update',
    getI18N: '/api.clientSideTranslation_admin.getI18N',
    updateI18N: '/api.clientSideTranslation_admin.updateI18N',
  },

  clientSideTranslation: {
    getAll: '/api.clientSideTranslation.getAll',
  },

  legalDocumentAdmin: {
    grid: '/api.legalDocument_admin.grid',
    export: '/api.legalDocument_admin.export',
    get: '/api.legalDocument_admin.get',
    getInfo: '/api.legalDocument_admin.getInfo',
    create: '/api.legalDocument_admin.create',
    update: '/api.legalDocument_admin.update',
    delete: '/api.legalDocument_admin.delete',
    enable: '/api.legalDocument_admin.enable',
    updateI18N: '/api.legalDocument_admin.updateI18N',
    getI18N: '/api.legalDocument_admin.getI18N',
  },

  legalFileItemAdmin: {
    grid: '/api.legalFileItem_admin.grid',
    export: '/api.legalFileItem_admin.export',
    get: '/api.legalFileItem_admin.get',
    create: '/api.legalFileItem_admin.create',
    delete: '/api.legalFileItem_admin.delete',
    download: '/api.legalFileItem_admin.download',
  },

  legalFileItemUser: {
    download: '/api.legalFileItem_user.download',
  },

  legalFileItem: {
    download: '/api.legalFileItem.download',
  },

  legalDocument: {
    list: '/api.legalDocument_user.list',
  },

  legalTradinPlatformAdmin: {
    grid: '/api.legalTradingPlatform_admin.grid',
    export: '/api.legalTradingPlatform_admin.export',
    get: '/api.legalTradingPlatform_admin.get',
    getInfo: '/api.legalTradingPlatform_admin.getInfo',
    create: '/api.legalTradingPlatform_admin.create',
    update: '/api.legalTradingPlatform_admin.update',
    delete: '/api.legalTradingPlatform_admin.delete',
    enable: '/api.legalTradingPlatform_admin.enable',
    getI18N: '/api.legalTradingPlatform_admin.getI18N',
    updateI18N: '/api.legalTradingPlatform_admin.updateI18N',
  },

  legalTradingPlatformUser: {
    list: '/api.legalTradingPlatform_user.list',
  },

  recognia: {
    getPages: '/api.recognia.getPages',
    page: '/api.recognia.page',
    getWidgets: '/api.recognia.getWidgets',
    getToken: '/api.recognia.getToken',
    getScriptUrl: '/api.recognia.getScriptUrl',
  },

  kycVerification: {
    get: '/api.kycVerification_admin.get',
    grid: '/api.kycVerification_admin.grid',
    export: '/api.kycVerification_admin.export',
    getUserFixedInfo: '/api.kycVerification_admin.getUserFixedInfo',
    updateUserFixedInfo: '/api.kycVerification_admin.updateUserFixedInfo',
    trigger: '/api.kycVerification_admin.trigger',
    updateFollowedUpBy: '/api.kycVerification_admin.updateFollowedUpBy',
    updateFollowedUpByWithOverride: '/api.kycVerification_admin.updateFollowedUpByWithOverride',
  },

  kycVerificationApiLog: {
    get: '/api.kycVerificationApiLog_admin.get',
    grid: '/api.kycVerificationApiLog_admin.grid',
  },

  kycVerificationCallbackLog: {
    get: '/api.kycVerificationCallbackLog_admin.get',
    grid: '/api.kycVerificationCallbackLog_admin.grid',
  },

  kytVerificationAdmin: {
    grid: '/api.kytVerification_admin.grid',
    export: '/api.kytVerification_admin.export',
    get: '/api.kytVerification_admin.get',
    getInfo: '/api.kytVerification_admin.getInfo',
  },

  kytVerificationOperationAdmin: {
    grid: '/api.kytVerificationOperation_admin.grid',
    export: '/api.kytVerificationOperation_admin.export',
    get: '/api.kytVerificationOperation_admin.get',
    getInfo: '/api.kytVerificationOperation_admin.getInfo',
    trigger: '/api.kytVerificationOperation_admin.trigger',
  },

  kytVerificationWithdrawalAttemptAdmin: {
    grid: '/api.kytVerificationWithdrawalAttempt_admin.grid',
    export: '/api.kytVerificationWithdrawalAttempt_admin.export',
    get: '/api.kytVerificationWithdrawalAttempt_admin.get',
    getInfo: '/api.kytVerificationWithdrawalAttempt_admin.getInfo',
    trigger: '/api.kytVerificationWithdrawalAttempt_admin.trigger',
  },

  kytVerificationApiLogAdmin: {
    grid: '/api.kytVerificationApiLog_admin.grid',
    export: '/api.kytVerificationApiLog_admin.export',
    get: '/api.kytVerificationApiLog_admin.get',
    getInfo: '/api.kytVerificationApiLog_admin.getInfo',
  },

  faqAdmin: {
    grid: '/api.faq_admin.grid',
    export: '/api.faq_admin.export',
    get: '/api.faq_admin.get',
    getInfo: '/api.faq_admin.getInfo',
    create: '/api.faq_admin.create',
    update: '/api.faq_admin.update',
    enable: '/api.faq_admin.enable',
    delete: '/api.faq_admin.delete',
    getI18N: '/api.faq_admin.getI18N',
    updateI18N: '/api.faq_admin.updateI18N',
  },

  faqUser: {
    getList: '/api.faq_user.getList',
    questions: '/api.faq_user.questions',
    answer: '/api.faq_user.answer',
  },

  urlDomainAdmin: {
    grid: '/api.urlDomain_admin.grid',
    export: '/api.urlDomain_admin.export',
    create: '/api.urlDomain_admin.create',
    get: '/api.urlDomain_admin.get',
    getInfo: '/api.urlDomain_admin.getInfo',
    update: '/api.urlDomain_admin.update',
    delete: '/api.urlDomain_admin.delete',
  },

  unverifiedUsers: {
    grid: '/api.unverifiedUsers.grid',
    enqueueBatch: '/api.unverifiedUsers.enqueueBatch',
    enqueueBatchFromImport: '/api.unverifiedUsers.enqueueBatchFromImport',
  },

  unverifiedUsersBatch: {
    grid: '/api.unverifiedUsersBatch.grid',
    export: '/api.unverifiedUsersBatch.export',
    get: '/api.unverifiedUsersBatch.get',
  },

  unverifiedUsersBatchUser: {
    grid: '/api.unverifiedUsersBatchUser.grid',
    export: '/api.unverifiedUsersBatchUser.export',
  },

  hubSpot: {
    getToken: '/api.hubSpot.getToken',
  },

  crmEntityAdmin: {
    grid: '/api.crmEntity_admin.grid',
    export: '/api.crmEntity_admin.export',
    delete: '/api.crmEntity_admin.delete',
    merge: '/api.crmEntity_admin.merge',
  },

  userImport: {
    importUsers: '/api.userImport_admin.importUsers',
  },

  accountType: {
    getMasterType: '/api.masterAccount_user.getTypes',
    getInvestorType: '/api.investorAccount_user.getTypes',
    getAccountsForStartCopying: '/api.investorAccount_user.getAccountsForStartCopying',
    startCopying: '/api.investorAccount_user.startCopying',
    searchMastersRatingInfo: '/api.masterAccount_user.searchMastersRatingInfo',
    getMasterRatingToStartCopying: '/api.masterAccount_user.getMasterRatingToStartCopying',
    getAccountTypeFilter: '/api.masterAccount_user.getMasterRatingAccountTypes',
  },

  copyTradingSettings: {
    get: '/api.copyTradingSettings_user.get',
    getForMaster: '/api.copyTradingSettings_user.getForMaster',
    getForInvestor: '/api.copyTradingSettings_user.getForInvestor',
  },

  copyTradingAdminSettings: {
    get: '/api.copyTradingSettings_admin.get',
  },

  appVersionAdmin: {
    grid: '/api.appVersion_admin.grid',
    export: '/api.appVersion_admin.export',
    get: '/api.appVersion_admin.get',
    getInfo: '/api.appVersion_admin.getInfo',
    create: '/api.appVersion_admin.create',
    update: '/api.appVersion_admin.update',
    delete: '/api.appVersion_admin.delete',
    getList: '/api.appVersion_admin.getList',
  },

  copyTrading: {
    getMyAccountsByMaster: '/api.investorAccount_user.getMyAccountsByMaster',
    stopCopying: '/api.investorAccount_user.stopCopying',
    stopCopyingByMaster: '/api.masterAccount_user.stopCopying',
    getMasterProfile: '/api.masterProfile_user.get',
    getMasterAccounts: '/api.masterProfile_user.getAccounts',
    getTradingVolumeChart: '/api.masterProfile_user.getTradingVolumeChart',
    getAccountProfitability: '/api.masterProfile_user.getProfitabilityChart',
    getTrades: '/api.masterProfile_user.getTrades',
  },

  investorProfileAdmin: {
    get: '/api.investorProfile_admin.get',
  },

  investorAccountAdmin: {
    grid: '/api.investorAccount_admin.grid',
    export: '/api.investorAccount_admin.export',
    get: '/api.investorAccount_admin.get',
    getInfo: '/api.investorAccount_admin.getInfo',
    update: '/api.investorAccount_admin.update',
    stopCopy: '/api.investorAccount_admin.stopCopying',
  },

  investorAccountSettingsAdmin: {
    updateSettings: '/api.investorAccountSettings_admin.update',
    getSettings: '/api.investorAccountSettings_admin.get',
  },

  masterProfileAdmin: {
    get: '/api.masterProfile_admin.get',
    getProfitabilityChart: '/api.masterProfile_admin.getProfitabilityChart',
  },

  masterAccountAdmin: {
    grid: '/api.masterAccount_admin.grid',
    export: '/api.masterAccount_admin.export',
    update: '/api.masterAccount_admin.update',
    get: '/api.masterAccount_admin.get',
    getAccountProfile: '/api.masterAccount_admin.getAccountProfile',
    getProfitabilityChart: '/api.masterAccount_admin.getProfitabilityChart',
  },

  copyTradingProfileAdmin: {
    getInfo: '/api.copyTradingProfile_admin.getInfo',
  },

  ftdUtmReport: {
    grid: '/api.ftdUtmReport_admin.grid',
    export: '/api.ftdUtmReport_admin.export',
  },

  jurisdictionUser: {
    getJurisdictionAgreement: '/api.userJurisdiction_user.getJurisdictionAgreement',
    acceptAgreement: '/api.userJurisdiction_user.acceptAgreement',
  },

  jurisdictionAdmin: {
    grid: '/api.userJurisdiction_admin.grid',
    update: '/api.userJurisdiction_admin.update',
  },

  recurringJobs: {
    sync: '/api.recurringJobs_admin.sync',
  },

  backgroundJobHistoryAdmin: {
    grid: '/api.backgroundJobHistory_admin.grid',
    export: '/api.backgroundJobHistory_admin.export',
    get: '/api.backgroundJobHistory_admin.get',
    getInfo: '/api.backgroundJobHistory_admin.getInfo',
    repeat: '/api.backgroundJobHistory_admin.repeat',
  },

  emailSubscriptionUser: {
    get: '/api.emailSubscription_user.get',
    update: '/api.emailSubscription_user.update',
  },

  pushSubscriptionUser: {
    get: '/api.pushSubscription_user.get',
    update: '/api.pushSubscription_user.update',
  },

  customNotificationReceivedUserAdmin: {
    grid: '/api.customNotificationReceivedUser_admin.grid',
    export: '/api.customNotificationReceivedUser_admin.export',
  },

  customNotificationNotificationGroupAdmin: {
    grid: '/api.customNotificationNotificationGroup_admin.grid',
    export: '/api.customNotificationNotificationGroup_admin.export',
    get: '/api.customNotificationNotificationGroup_admin.get',
    create: '/api.customNotificationNotificationGroup_admin.create',
    delete: '/api.customNotificationNotificationGroup_admin.delete',
  },

  customNotificationAdmin: {
    grid: '/api.customNotification_admin.grid',
    export: '/api.customNotification_admin.export',
    get: '/api.customNotification_admin.get',
    getInfo: '/api.customNotification_admin.getInfo',
    create: '/api.customNotification_admin.create',
    clone: '/api.customNotification_admin.clone',
    update: '/api.customNotification_admin.update',
    archive: '/api.customNotification_admin.archive',
    delete: '/api.customNotification_admin.delete',
    sendTestEmail: '/api.customNotification_admin.sendTestEmail',
    getScheduledDate: '/api.customNotification_admin.getScheduledDate',
    setScheduledDate: '/api.customNotification_admin.setScheduledDate',
    preview: '/api.customNotification_admin.preview',
    schedule: '/api.customNotification_admin.schedule',
    cancel: '/api.customNotification_admin.cancel',
    retry: '/api.customNotification_admin.retry',
  },

  customPushNotificationReceivedUserAdmin: {
    grid: '/api.customPushNotificationReceivedUser_admin.grid',
    export: '/api.customPushNotificationReceivedUser_admin.export',
  },

  customPushNotificationNotificationGroupAdmin: {
    grid: '/api.customPushNotificationNotificationGroup_admin.grid',
    export: '/api.customPushNotificationNotificationGroup_admin.export',
    get: '/api.customPushNotificationNotificationGroup_admin.get',
    create: '/api.customPushNotificationNotificationGroup_admin.create',
    delete: '/api.customPushNotificationNotificationGroup_admin.delete',
  },

  customPushNotificationAdmin: {
    grid: '/api.customPushNotification_admin.grid',
    export: '/api.customPushNotification_admin.export',
    get: '/api.customPushNotification_admin.get',
    getInfo: '/api.customPushNotification_admin.getInfo',
    create: '/api.customPushNotification_admin.create',
    clone: '/api.customPushNotification_admin.clone',
    update: '/api.customPushNotification_admin.update',
    archive: '/api.customPushNotification_admin.archive',
    delete: '/api.customPushNotification_admin.delete',
    sendTestPush: '/api.customPushNotification_admin.sendTestPush',
    getScheduledDate: '/api.customPushNotification_admin.getScheduledDate',
    setScheduledDate: '/api.customPushNotification_admin.setScheduledDate',
    preview: '/api.customPushNotification_admin.preview',
    schedule: '/api.customPushNotification_admin.schedule',
    cancel: '/api.customPushNotification_admin.cancel',
    retry: '/api.customPushNotification_admin.retry',
  },

  emailTemplateAdmin: {
    grid: '/api.emailTemplate_admin.grid',
    export: '/api.emailTemplate_admin.export',
    get: '/api.emailTemplate_admin.get',
    getInfo: '/api.emailTemplate_admin.getInfo',
    create: '/api.emailTemplate_admin.create',
    update: '/api.emailTemplate_admin.update',
    clone: '/api.emailTemplate_admin.clone',
    archive: '/api.emailTemplate_admin.archive',
    delete: '/api.emailTemplate_admin.delete',
    getList: '/api.emailTemplate_admin.getList',
  },

  notificationGroupAdmin: {
    grid: '/api.notificationGroup_admin.grid',
    export: '/api.notificationGroup_admin.export',
    get: '/api.notificationGroup_admin.get',
    getInfo: '/api.notificationGroup_admin.getInfo',
    create: '/api.notificationGroup_admin.create',
    update: '/api.notificationGroup_admin.update',
    delete: '/api.notificationGroup_admin.delete',
    getList: '/api.notificationGroup_admin.getList',
  },

  notificationCategoryAdmin: {
    grid: '/api.notificationCategory_admin.grid',
    export: '/api.notificationCategory_admin.export',
    get: '/api.notificationCategory_admin.get',
    getInfo: '/api.notificationCategory_admin.getInfo',
    create: '/api.notificationCategory_admin.create',
    clone: '/api.notificationCategory_admin.clone',
    update: '/api.notificationCategory_admin.update',
    delete: '/api.notificationCategory_admin.delete',
    getI18N: '/api.notificationCategory_admin.getI18N',
    updateI18N: '/api.notificationCategory_admin.updateI18N',
    getList: '/api.notificationCategory_admin.getList',
  },

  notificationGroupMemberAdmin: {
    grid: '/api.notificationGroupMember_admin.grid',
    export: '/api.notificationGroupMember_admin.export',
    get: '/api.notificationGroupMember_admin.get',
    create: '/api.notificationGroupMember_admin.create',
    delete: '/api.notificationGroupMember_admin.delete',
    preImport: '/api.notificationGroupMember_admin.preImport',
    import: '/api.notificationGroupMember_admin.import',
  },

  notificationGroupFilterAdmin: {
    grid: '/api.notificationGroupFilter_admin.grid',
    export: '/api.notificationGroupFilter_admin.export',
    get: '/api.notificationGroupFilter_admin.get',
    create: '/api.notificationGroupFilter_admin.create',
    delete: '/api.notificationGroupFilter_admin.delete',
  },

  loyaltyProgramAdmin: {
    grid: '/api.loyaltyProgram_admin.grid',
    export: '/api.loyaltyProgram_admin.export',
    get: '/api.loyaltyProgram_admin.get',
    getInfo: '/api.loyaltyProgram_admin.getInfo',
    preview: '/api.loyaltyProgram_admin.preview',
    create: '/api.loyaltyProgram_admin.create',
    clone: '/api.loyaltyProgram_admin.clone',
    update: '/api.loyaltyProgram_admin.update',
    enable: '/api.loyaltyProgram_admin.enable',
    show: '/api.loyaltyProgram_admin.show',
    delete: '/api.loyaltyProgram_admin.delete',
    getI18N: '/api.loyaltyProgram_admin.getI18N',
    updateI18N: '/api.loyaltyProgram_admin.updateI18N',
    getList: '/api.loyaltyProgram_admin.getList',
  },

  loyaltyProgramDocumentAdmin: {
    grid: '/api.loyaltyProgramDocument_admin.grid',
    export: '/api.loyaltyProgramDocument_admin.export',
    create: '/api.loyaltyProgramDocument_admin.create',
    delete: '/api.loyaltyProgramDocument_admin.delete',
    download: '/api.loyaltyProgramDocument_admin.download',
  },

  loyaltyProgramParticipantAdmin: {
    grid: '/api.loyaltyProgramParticipant_admin.grid',
    export: '/api.loyaltyProgramParticipant_admin.export',
    get: '/api.loyaltyProgramParticipant_admin.get',
    getInfo: '/api.loyaltyProgramParticipant_admin.getInfo',
    update: '/api.loyaltyProgramParticipant_admin.update',
    addComment: '/api.loyaltyProgramParticipant_admin.addComment',
  },

  loyaltyProgramProcessorAdmin: {
    grid: '/api.loyaltyProgramProcessor_admin.grid',
    export: '/api.loyaltyProgramProcessor_admin.export',
    get: '/api.loyaltyProgramProcessor_admin.get',
    getInfo: '/api.loyaltyProgramProcessor_admin.getInfo',
    update: '/api.loyaltyProgramProcessor_admin.update',
    create: '/api.loyaltyProgramProcessor_admin.create',
    clone: '/api.loyaltyProgramProcessor_admin.clone',
    delete: '/api.loyaltyProgramProcessor_admin.delete',
    enable: '/api.loyaltyProgramProcessor_admin.enable',
    getI18N: '/api.loyaltyProgramProcessor_admin.getI18N',
    updateI18N: '/api.loyaltyProgramProcessor_admin.updateI18N',
  },

  loyaltyProgramRedeemAdmin: {
    grid: '/api.loyaltyProgramRedeem_admin.grid',
    export: '/api.loyaltyProgramRedeem_admin.export',
    previewGrid: '/api.loyaltyProgramRedeem_admin.previewGrid',
    get: '/api.loyaltyProgramRedeem_admin.get',
    getInfo: '/api.loyaltyProgramRedeem_admin.getInfo',
    update: '/api.loyaltyProgramRedeem_admin.update',
    create: '/api.loyaltyProgramRedeem_admin.create',
    clone: '/api.loyaltyProgramRedeem_admin.clone',
    delete: '/api.loyaltyProgramRedeem_admin.delete',
    enable: '/api.loyaltyProgramRedeem_admin.enable',
    getI18N: '/api.loyaltyProgramRedeem_admin.getI18N',
    updateI18N: '/api.loyaltyProgramRedeem_admin.updateI18N',
  },

  loyaltyProgramOperationAdmin: {
    grid: '/api.loyaltyProgramOperation_admin.grid',
    export: '/api.loyaltyProgramOperation_admin.export',
    get: '/api.loyaltyProgramOperation_admin.get',
    getInfo: '/api.loyaltyProgramOperation_admin.getInfo',
    create: '/api.loyaltyProgramOperation_admin.create',
  },

  loyaltyProgramParticipantRedeemAdmin: {
    grid: '/api.loyaltyProgramParticipantRedeem_admin.grid',
    export: '/api.loyaltyProgramParticipantRedeem_admin.export',
    get: '/api.loyaltyProgramParticipantRedeem_admin.get',
    getInfo: '/api.loyaltyProgramParticipantRedeem_admin.getInfo',
    addComment: '/api.loyaltyProgramParticipantRedeem_admin.addComment',
    promote: '/api.loyaltyProgramParticipantRedeem_admin.promote',
  },

  loyaltyProgramUser: {
    grid: '/api.loyaltyProgram_user.grid',
    details: '/api.loyaltyProgram_user.details',
  },

  loyaltyProgramParticipantUser: {
    join: '/api.loyaltyProgramParticipant_user.join',
    stopParticipating: '/api.loyaltyProgramParticipant_user.stopParticipating',
    getInfo: '/api.loyaltyProgramParticipant_user.getInfo',
  },

  loyaltyProgramOperationUser: {
    grid: '/api.loyaltyProgramOperation_user.grid',
    export: '/api.loyaltyProgramOperation_user.export',
  },

  loyaltyProgramRedeemUser: {
    grid: '/api.loyaltyProgramRedeem_user.grid',
    details: '/api.loyaltyProgramRedeem_user.details',
    redeem: '/api.loyaltyProgramRedeem_user.redeem',
    getTradingAccounts: '/api.loyaltyProgramRedeem_user.getTradingAccounts',
    getWalletAccounts: '/api.loyaltyProgramRedeem_user.getWalletAccounts',
  },

  loyaltyProgramParticipantRedeemUser: {
    grid: '/api.loyaltyProgramParticipantRedeem_user.grid',
    export: '/api.loyaltyProgramParticipantRedeem_user.export',
    details: '/api.loyaltyProgramParticipantRedeem_user.details',
    cancel: '/api.loyaltyProgramParticipantRedeem_user.cancel',
  },

  loyaltyProgramDocumentUser: {
    download: '/api.loyaltyProgramDocument_user.download',
  },

  trustedDeviceAdmin: {
    grid: '/api.trustedDevice_admin.grid',
    export: '/api.trustedDevice_admin.export',
    get: '/api.trustedDevice_admin.get',
    getInfo: '/api.trustedDevice_admin.getInfo',
    delete: '/api.trustedDevice_admin.delete',
  },

  trustedDeviceUser: {
    grid: '/api.trustedDevice_user.grid',
    delete: '/api.trustedDevice_user.delete',
  },

  contestAdmin: {
    grid: '/api.contest_admin.grid',
    export: '/api.contest_admin.export',
    get: '/api.contest_admin.get',
    getInfo: '/api.contest_admin.getInfo',
    preview: '/api.contest_admin.preview',
    create: '/api.contest_admin.create',
    clone: '/api.contest_admin.clone',
    update: '/api.contest_admin.update',
    enable: '/api.contest_admin.enable',
    show: '/api.contest_admin.show',
    cancel: '/api.contest_admin.cancel',
    delete: '/api.contest_admin.delete',
    getI18N: '/api.contest_admin.getI18N',
    updateI18N: '/api.contest_admin.updateI18N',
  },

  contestTradingAccountTypeAdmin: {
    grid: '/api.contestTradingAccountType_admin.grid',
    export: '/api.contestTradingAccountType_admin.export',
    get: '/api.contestTradingAccountType_admin.get',
    create: '/api.contestTradingAccountType_admin.create',
    update: '/api.contestTradingAccountType_admin.update',
    delete: '/api.contestTradingAccountType_admin.delete',
  },

  contestDocumentAdmin: {
    grid: '/api.contestDocument_admin.grid',
    export: '/api.contestDocument_admin.export',
    create: '/api.contestDocument_admin.create',
    delete: '/api.contestDocument_admin.delete',
    download: '/api.contestDocument_admin.download',
  },

  contestParticipantAdmin: {
    grid: '/api.contestParticipant_admin.grid',
    export: '/api.contestParticipant_admin.export',
    get: '/api.contestParticipant_admin.get',
    getInfo: '/api.contestParticipant_admin.getInfo',
    show: '/api.contestParticipant_admin.show',
  },

  contestDocumentUser: {
    getContestDocuments: '/api.contestDocument_user.getContestDocuments',
    download: '/api.contestDocument_user.download',
  },

  contestUser: {
    grid: '/api.contest_user.grid',
    getDetails: '/api.contest_user.details',
    getParticipants: '/api.contestParticipant_user.grid',
  },

  fileImport: {
    grid: '/api.fileImport_admin.grid',
  },

  galleryFileItemAdmin: {
    grid: '/api.galleryFileItem_admin.grid',
    delete: '/api.galleryFileItem_admin.delete',
    upload: '/api.galleryFileItem_admin.upload',
    download: '/api.galleryFileItem_admin.download',
  },

  companyAccountAdmin: {
    grid: '/api.companyAccount_admin.grid',
    export: '/api.companyAccount_admin.export',
    get: '/api.companyAccount_admin.get',
    getInfo: '/api.companyAccount_admin.getInfo',
    create: '/api.companyAccount_admin.create',
    update: '/api.companyAccount_admin.update',
    enable: '/api.companyAccount_admin.enable',
    delete: '/api.companyAccount_admin.delete',
    getNames: '/api.companyAccount_admin.getNames',
    getBankNames: '/api.companyAccount_admin.getBankNames',
    getCurrencies: '/api.companyAccount_admin.getCurrencies',
    getAvailableCurrencies: '/api.companyAccount_admin.getAvailableCurrencies',
  },

  companyTransferAdmin: {
    grid: '/api.companyTransfer_admin.grid',
    export: '/api.companyTransfer_admin.export',
    get: '/api.companyTransfer_admin.get',
    getInfo: '/api.companyTransfer_admin.getInfo',
    create: '/api.companyTransfer_admin.create',
    update: '/api.companyTransfer_admin.update',
    delete: '/api.companyTransfer_admin.delete',
  },

  companyTransferAttachmentAdmin: {
    grid: '/api.companyTransferAttachment_admin.grid',
    export: '/api.companyTransferAttachment_admin.export',
    create: '/api.companyTransferAttachment_admin.create',
    download: '/api.companyTransferAttachment_admin.download',
    delete: '/api.companyTransferAttachment_admin.delete',
  },

  importItemAdmin: {
    grid: '/api.importItem_admin.grid',
    export: '/api.importItem_admin.export',
    get: '/api.importItem_admin.get',
    getInfo: '/api.importItem_admin.getInfo',
    create: '/api.importItem_admin.create',
    update: '/api.importItem_admin.update',
    delete: '/api.importItem_admin.delete',
    download: '/api.importItem_admin.download',
    logMessage: '/api.importItem_admin.logMessage',
    validate: '/api.importItem_admin.validate',
    import: '/api.importItem_admin.import',
    template: '/api.importItem_admin.template',
  },

  importItemRecordAdmin: {
    grid: '/api.importItemRecord_admin.grid',
    export: '/api.importItemRecord_admin.export',
    get: '/api.importItemRecord_admin.get',
    logMessage: '/api.importItemRecord_admin.logMessage',
  },

  userAccessAdmin: {
    grid: '/api.userAccess_admin.grid',
    export: '/api.userAccess_admin.export',
    get: '/api.userAccess_admin.get',
    create: '/api.userAccess_admin.create',
    update: '/api.userAccess_admin.update',
    addComment: '/api.userAccess_admin.addComment',
    delete: '/api.userAccess_admin.delete',
  },

  marketingBannerCategoryAdmin: {
    grid: '/api.marketingBannerCategory_admin.grid',
    export: '/api.marketingBannerCategory_admin.export',
    get: '/api.marketingBannerCategory_admin.get',
    getInfo: '/api.marketingBannerCategory_admin.getInfo',
    getList: '/api.marketingBannerCategory_admin.getList',
    create: '/api.marketingBannerCategory_admin.create',
    update: '/api.marketingBannerCategory_admin.update',
    delete: '/api.marketingBannerCategory_admin.delete',
    enable: '/api.marketingBannerCategory_admin.enable',
  },

  marketingBannerFileAdmin: {
    grid: '/api.marketingBannerFile_admin.grid',
    export: '/api.marketingBannerFile_admin.export',
    upload: '/api.marketingBannerFile_admin.upload',
    download: '/api.marketingBannerFile_admin.download',
    create: '/api.marketingBannerFile_admin.create',
    enable: '/api.marketingBannerFile_admin.enable',
  },

  dataChangeRequestUser: {
    showDataChangeRequestPopup: '/api.dataChangeRequest_user.showDataChangeRequestPopup',
    create: '/api.dataChangeRequest_user.create',
  },

  features: {
    isEnabled: '/api.features.isEnabled',
    getAll: '/api.features.getAll',
  },

  userUISettingsAdmin: {
    grid: '/api.userUISettings_admin.grid',
    export: '/api.userUISettings_admin.export',
    get: '/api.userUISettings_admin.get',
    getInfo: '/api.userUISettings_admin.getInfo',
  },
});

export default ApiUrls;
